import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import LoadingSpinner from '@/components/LoadingSpinner';
import { fetchProdutosERP, fetchProdutosPGA, produtoKeys } from '@/services/produtoService';
import { parseApiError } from '@/utils/apiError';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cadastrosCodigoMapaSearchSchema, type TCadastrosCodigoMapaSearchFormData } from './schema';

export const Route = createFileRoute('/_authenticated/cadastros-codigo-mapa/')({
  component: CadastrosCodigoMapa,
});

type TTab = 'pga' | 'erp';

function CadastrosCodigoMapa() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTab, setSelectedTab] = useState<TTab>('pga');

  const { register, handleSubmit, control, resetField } = useForm<TCadastrosCodigoMapaSearchFormData>({
    resolver: zodResolver(cadastrosCodigoMapaSearchSchema),
    defaultValues: { search: '' },
  });
  const searchTerm = useWatch({ control, name: 'search' });

  const params = { page: currentPage, page_size: pageSize, search: searchTerm };

  const pgaQuery = useQuery({
    queryKey: produtoKeys.pga(params),
    queryFn: () => fetchProdutosPGA(params),
    enabled: selectedTab === 'pga',
  });
  const erpQuery = useQuery({
    queryKey: produtoKeys.erp(params),
    queryFn: () => fetchProdutosERP(params),
    enabled: selectedTab === 'erp',
  });

  const activeQuery = selectedTab === 'pga' ? pgaQuery : erpQuery;
  const { isLoading, isError, error } = activeQuery;
  const produtosPga = selectedTab === 'pga' ? (pgaQuery.data?.items ?? []) : [];
  const produtosErp = selectedTab === 'erp' ? (erpQuery.data?.items ?? []) : [];
  const totalPages = activeQuery.data?.total_pages ?? 1;

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab as TTab);
    resetField('search');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const onSubmitSearch = () => {
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Cadastros de Código MAPA</h1>
        <p className="text-on-surface-variant">Gerenciar produtos do PGA e do ERP</p>
      </div>

      <div className="flex flex-col gap-4 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
        <Tabs
          value={selectedTab}
          onValueChange={handleTabChange}
        >
          <TabsList>
            <TabsTrigger value="pga">Códigos PGA</TabsTrigger>
            <TabsTrigger value="erp">Produtos ERP</TabsTrigger>
          </TabsList>
        </Tabs>

        <form
          onSubmit={(e) => void handleSubmit(onSubmitSearch)(e)}
          noValidate
        >
          <div className="flex items-end gap-4">
            <FieldGroup className="flex-1">
              <Field>
                <FieldLabel htmlFor="search">Buscar {selectedTab === 'pga' ? 'Códigos PGA' : 'Produtos ERP'}</FieldLabel>
                <Input
                  type="text"
                  id="search"
                  placeholder="Digite para buscar…"
                  autoComplete="off"
                  {...register('search')}
                />
              </Field>
            </FieldGroup>
            <Button type="submit">Buscar</Button>
          </div>
        </form>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex items-center justify-between border-b border-outline-variant p-4">
          <h2 className="text-lg font-semibold text-on-surface">Lista de Produtos</h2>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {[10, 25, 50, 100].map((size) => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                  >
                    Itens por página: {size}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : isError ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-lg bg-error-container p-4 text-on-error-container"
            >
              Erro ao carregar os dados: {parseApiError(error, 'Erro ao buscar produtos')}
            </div>
          ) : selectedTab === 'pga' && produtosPga.length === 0 ? (
            <EmptyProdutos />
          ) : selectedTab === 'erp' && produtosErp.length === 0 ? (
            <EmptyProdutos />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código {selectedTab === 'pga' ? 'PGA' : 'ERP'}</TableHead>
                  <TableHead>Descrição</TableHead>
                  {selectedTab === 'pga' && <TableHead>Categoria</TableHead>}
                  {selectedTab === 'pga' && <TableHead>Área</TableHead>}
                  <TableHead>Unidade</TableHead>
                  {selectedTab === 'erp' && <TableHead>Unidade Vínculada</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedTab === 'pga'
                  ? produtosPga.map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>{produto.cod_produto}</TableCell>
                        <TableCell>{produto.descricao}</TableCell>
                        <TableCell>{produto.categoria}</TableCell>
                        <TableCell>{produto.area}</TableCell>
                        <TableCell>{produto.unidade_medida}</TableCell>
                      </TableRow>
                    ))
                  : produtosErp.map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>{produto.sku}</TableCell>
                        <TableCell>{produto.descricao}</TableCell>
                        <TableCell>{produto.unidade_medida}</TableCell>
                        <TableCell>{produto.unidade_nome}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 border-t border-outline-variant p-4">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Anterior
          </Button>
          <span className="text-sm text-on-surface-variant">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyProdutos() {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>Nenhum produto encontrado</EmptyTitle>
        <EmptyDescription>Tente ajustar os filtros de busca.</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
